// src/components/GlobalFunction/ProductCard.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { getLangFromCookie } from "../../Utils/LangCookie";
import { resolveAssetUrl } from "../../lib/http";
import { resolveCanonicalUserID } from "../../Utils/UserIdentity";
import SehoLoadingSpinner from "./SehoLoadingSpinner";
// ✅ favourite API
import { FavouriteAPI } from "../../api/Favourite";

// ✅ auth fallback (so it works even if userID prop not passed)
import { useAuth } from "../pages/Auth/AuthContext";

/** Try to extract a URL from any shape: string / {url} / WPGraphQL media objects */
function toUrl(v) {
  if (!v) return undefined;
  if (typeof v === "string") return v;

  if (typeof v === "object") {
    if (v.url) return v.url;
    if (v.image) return v.image;

    if (v.sourceUrl) return v.sourceUrl;
    if (v.mediaItemUrl) return v.mediaItemUrl;

    if (v.node && (v.node.sourceUrl || v.node.mediaItemUrl))
      return v.node.sourceUrl || v.node.mediaItemUrl;
  }
  return undefined;
}

/** Localized field getter for new WP output (best-effort) */
function getLocalizedField(product, baseKey, lang) {
  const L = (lang || "ZH").toUpperCase();
  const main = product?.main || product?.Main || {};

  const trim = (x) =>
    typeof x === "string" ? x.trim() : x == null ? "" : String(x).trim();

  const direct = trim(main?.[baseKey]);
  if (direct && L === "ZH") return direct;

  const suffixKeys =
    L === "EN"
      ? [`${baseKey}_EN`, `${baseKey}EN`, `${baseKey}_En`, `${baseKey}En`]
      : L === "JP"
        ? [`${baseKey}_JP`, `${baseKey}JP`, `${baseKey}_Jp`, `${baseKey}Jp`]
        : [];

  for (const k of suffixKeys) {
    const v = trim(main?.[k]);
    if (v) return v;
  }

  const candidates = [
    product?.mainTranslations?.[L]?.[baseKey],
    product?.mainTranslation?.[L]?.[baseKey],
    main?.translations?.[L]?.[baseKey],
    main?.translation?.[L]?.[baseKey],
    product?.translations?.main?.[L]?.[baseKey],
    product?.translation?.main?.[L]?.[baseKey],
  ].map(trim);

  const t = candidates.find(Boolean);
  if (t) return t;

  if (direct) return direct;

  return "";
}

function computeDisplayName(product, lang) {
  const trim = (v) =>
    typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();

  const mainNameOld = trim(product?.main?.name);
  if (mainNameOld) return mainNameOld;

  const variantNameOld = trim(product?.main?.variantName);
  if (variantNameOld) return variantNameOld;

  const name = getLocalizedField(product, "產品名稱", lang);
  if (name) return name;

  return (
    trim(getLocalizedField(product, "商品系列名稱", lang)) ||
    trim(
      product?.productName ??
        product?.name ??
        product?.ProductName ??
        product?.Name,
    )
  );
}

/** Extract images from new WP schema (and keep old shapes if still around) */
function extractImages(product) {
  const main = product?.main || product?.Main || {};

  const pools = [
    ...(Array.isArray(product?.images) ? product.images : []),
    product?.image,

    ...(Array.isArray(main?.images) ? main.images : []),
    ...(Array.isArray(main?.圖片) ? main.圖片 : []),
    ...(Array.isArray(main?.圖) ? main.圖 : []),
    ...(Array.isArray(main?.gallery) ? main.gallery : []),
    ...(Array.isArray(main?.相簿) ? main.相簿 : []),

    main?.image,
    main?.主圖,
    main?.主圖URL,
    main?.主圖Url,
    main?.thumbnail,
    main?.縮圖,

    product?.featuredImage,
    product?.featuredImage?.node,
  ];

  const urls = pools
    .map(toUrl)
    .filter(Boolean)
    .map(resolveAssetUrl)
    .filter(Boolean);

  const seen = new Set();
  const out = [];
  for (const u of urls) {
    if (!seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

function asBoolFavourite(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (v === 1 || v === "1") return true;
  if (v === 0 || v === "0") return false;
  return false;
}

function getProductIdAny(product) {
  return (
    product?.productID ||
    product?.productId ||
    product?.id ||
    product?.main?.["編號"] ||
    product?.main?.productID ||
    ""
  );
}

function resolveUserIdAny(user) {
  return resolveCanonicalUserID(user);
}

// ✅ robust auth check (works across older/newer AuthContext shapes)
function isAuthed(user) {
  if (!user) return false;
  if (user?.isLoggedIn === true) return true;
  if (user?.loginStatus === true) return true;
  return !!resolveUserIdAny(user);
}

function getWpHeadlessUploadFallback(src) {
  const raw = String(src || "").trim();
  if (!raw) return "";

  try {
    const u = new URL(raw);
    if (u.pathname.startsWith("/wp-headless/wp-content/uploads/")) {
      u.pathname = u.pathname.replace(
        /^\/wp-headless\/wp-content\/uploads\//,
        "/wp-content/uploads/",
      );
      return u.toString();
    }
  } catch {
    if (raw.startsWith("/wp-headless/wp-content/uploads/")) {
      return raw.replace(
        /^\/wp-headless\/wp-content\/uploads\//,
        "/wp-content/uploads/",
      );
    }
  }

  return "";
}

export default function ProductCard({
  fontstyle = "LINE Seed TW",
  product,
  to,
  view = "grid",
  compact = false,
  imageHeight = 160,
  titleLines = 1,
  onClickCard,

  // ✅ favourite
  showFavourite = true,
  userID = "",
  onFavouriteChange,
  disableHoverSwap = false,
}) {
  const lang = getLangFromCookie(); // "ZH" | "EN" | "JP"
  const { user } = useAuth?.() || {};

  const navigate = useNavigate();
  const loc = useLocation();

  const loggedIn = isAuthed(user);

  const effectiveUserID = useMemo(() => {
    const direct = String(userID || "").trim();
    if (direct) return direct;
    return String(resolveUserIdAny(user) || "").trim();
  }, [userID, user]);

  const imgs = useMemo(() => extractImages(product), [product]);
  const [imgIdx, setImgIdx] = useState(0);

  const productID = useMemo(
    () => String(getProductIdAny(product) || "").trim(),
    [product],
  );

  const favFromProduct = useMemo(
    () => asBoolFavourite(product?.favourite),
    [product],
  );

  const [favorited, setFavorited] = useState(favFromProduct);
  const [favBusy, setFavBusy] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setFavorited(favFromProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favFromProduct, productID]);

  const displayName = useMemo(
    () => computeDisplayName(product, lang),
    [product, lang],
  );

  const imgSrc = imgs[imgIdx] || imgs[0];
  const displayImageSrc = imgSrc || "/img/empty.webp";
  const activeImageSrcRef = useRef(displayImageSrc);
  const [imageReady, setImageReady] = useState(false);

  activeImageSrcRef.current = displayImageSrc;

  useLayoutEffect(() => {
    setImageReady(false);
  }, [displayImageSrc, productID]);

  const isB2B =
    /(^|\/)b2b(\/|$)/i.test(to || "") ||
    /(^|\/)b2b(\/|$)/i.test(product?.categoryHref || "");

  const handleEnter = () => {
    if (disableHoverSwap) return;
    if (imgs.length > 1) setImgIdx(1);
  };
  const handleLeave = () => setImgIdx(0);

  // ✅ keep old behavior: CardActionArea is a RouterLink only when "to" exists
  const actionAreaProps = (() => {
    if (!to) return { component: "div", role: "button" };
    return { component: RouterLink, to };
  })();

  // ✅ redirect helper for any "auth required" action
  const redirectToAuth = () => {
    const path = loc?.pathname || "/";
    const search = loc?.search || "";
    const hash = loc?.hash || "";
    const redirectTo = `${path}${search}${hash}`;

    navigate(isB2B ? "/b2b/auth" : "/b2c/auth", {
      replace: false,
      state: { redirectTo },
    });
  };

  // ✅ helper: mimic cart behavior (dispatch event that Navbar listens to)
  const emitFavouriteUpdated = (detail = {}) => {
    try {
      window.dispatchEvent(new CustomEvent("favourite:updated", { detail }));
    } catch {
      // ignore
    }
  };

  /**
   * ✅ favourite safety:
   * - if NOT logged in (or token expired => AuthContext cleared) => redirect to auth
   * - otherwise call favourite API
   */
  const toggleFavorite = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (!showFavourite) return;

    if (!loggedIn) {
      redirectToAuth();
      return;
    }

    if (!productID) return;

    const uid = String(effectiveUserID || "").trim();
    if (!uid) {
      redirectToAuth();
      return;
    }

    if (favBusy) return;
    setFavBusy(true);

    const next = !favorited;

    // optimistic UI
    setFavorited(next);
    onFavouriteChange?.(next, productID);

    try {
      if (next) await FavouriteAPI.add(uid, productID);
      else await FavouriteAPI.remove(uid, productID);

      // ✅ IMPORTANT: only after API success, notify Navbar to refresh count
      // This matches how inquiry badge stays accurate.
      emitFavouriteUpdated({
        reason: next ? "added" : "removed",
        productID: String(productID),
      });
    } catch (err) {
      console.error("Favourite toggle failed:", err);

      // If backend says unauthorized, redirect
      const code = err?.response?.status;
      if (code === 401 || code === 403) {
        if (mountedRef.current) {
          setFavorited(!next);
          onFavouriteChange?.(!next, productID);
          setFavBusy(false);
        }

        // ✅ keep badge consistent after rollback
        emitFavouriteUpdated({
          reason: "rollback-auth",
          productID: String(productID),
        });

        redirectToAuth();
        return;
      }

      // rollback
      if (mountedRef.current) {
        setFavorited(!next);
        onFavouriteChange?.(!next, productID);
      }

      // ✅ keep badge consistent after rollback
      emitFavouriteUpdated({
        reason: "rollback",
        productID: String(productID),
      });
    } finally {
      if (mountedRef.current) setFavBusy(false);
    }
  };

  const handleProductImageLoad = async (event) => {
    const imageElement = event.currentTarget;
    const requestedSource = activeImageSrcRef.current;

    try {
      if (typeof imageElement.decode === "function") {
        await imageElement.decode();
      }
    } catch {
      // The browser may still display the image even when decode rejects.
    }

    if (!mountedRef.current) return;
    if (activeImageSrcRef.current !== requestedSource) return;

    setImageReady(true);
  };

  const handleProductImageError = (event) => {
    const imageElement = event.currentTarget;
    const currentSource = String(imageElement?.src || "");

    setImageReady(false);

    if (currentSource.endsWith("/img/empty.webp")) {
      setImageReady(true);
      return;
    }

    const triedWpRootFallback =
      imageElement.dataset.triedWpRootFallback === "1";
    const fallback = getWpHeadlessUploadFallback(currentSource);

    if (!triedWpRootFallback && fallback) {
      imageElement.dataset.triedWpRootFallback = "1";
      imageElement.src = fallback;
      return;
    }

    imageElement.src = "/img/empty.webp";
  };

  const mediaProps = {
    component: "img",
    image: displayImageSrc,
    alt: displayName || "",
    loading: "lazy",
    fetchPriority: "low",
    decoding: "async",

    /*
     * Database images use their own card spinner and must not be included
     * in the future full-screen website-image loading check.
     */
    "data-global-loading-ignore": "true",

    onLoad: handleProductImageLoad,
    onError: handleProductImageError,
  };

  const renderProductImage = ({ frameSx = {}, imageSx = {} } = {}) => (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        bgcolor: "#fff",
        ...frameSx,
      }}
    >
      {!imageReady && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
          }}
        >
          <SehoLoadingSpinner />
        </Box>
      )}

      <CardMedia
        key={`${productID}:${displayImageSrc}`}
        {...mediaProps}
        sx={{
          opacity: imageReady ? 1 : 0,
          transition: "opacity .2s ease",
          ...imageSx,
        }}
      />
    </Box>
  );

  // ✅ HIDE favourite icon when not logged in
  const allowFavouriteUI =
    !!showFavourite && loggedIn && !!effectiveUserID && !!productID;

  const HeartIcon = !allowFavouriteUI ? null : favorited ? (
    <FavoriteIcon
      onClick={toggleFavorite}
      sx={{
        fontSize: 20,
        color: "red",
        cursor: favBusy ? "not-allowed" : "pointer",
        opacity: favBusy ? 0.6 : 1,
        pointerEvents: "auto",
        transition: "transform .12s ease",
        "&:active": { transform: "scale(.92)" },
      }}
    />
  ) : (
    <FavoriteBorderIcon
      onClick={toggleFavorite}
      sx={{
        fontSize: 20,
        color: "text.disabled",
        cursor: favBusy ? "not-allowed" : "pointer",
        opacity: favBusy ? 0.6 : 1,
        pointerEvents: "auto",
        transition: "transform .12s ease",
        "&:active": { transform: "scale(.92)" },
      }}
    />
  );

  /* ============================ LIST VIEW ============================ */
  if (view === "list") {
    if (isB2B) {
      return (
        <Card
          sx={{
            overflow: "hidden",
            transition: "transform .15s ease, box-shadow .15s ease",
            "&:hover": { transform: "translateY(-2px)", boxShadow: 1 },
          }}
        >
          <CardActionArea
            {...actionAreaProps}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: HeartIcon ? "110px 1fr 50px" : "110px 1fr",
                sm: HeartIcon ? "140px 1fr 56px" : "140px 1fr",
              },
              alignItems: "center",
              columnGap: 2,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              minHeight: { xs: 120, sm: 150 },
              bgcolor: "background.paper",
            }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onClick={onClickCard}
          >
            {renderProductImage({
              frameSx: {
                width: "100%",
                height: "100%",
                minHeight: { xs: 100, sm: 130 },
                maxHeight: { xs: 120, sm: 150 },
                borderRadius: 1,
              },
              imageSx: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            })}

            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography
                variant="subtitle1"
                title={displayName}
                sx={{
                  fontFamily: fontstyle,
                  fontWeight: 600,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                }}
              >
                {displayName}
              </Typography>
            </Box>

            {HeartIcon && (
              <Box
                sx={{
                  justifySelf: "end",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {HeartIcon}
              </Box>
            )}
          </CardActionArea>
        </Card>
      );
    }

    // Non-B2B list layout
    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: "row",
          transition: "transform .15s ease, box-shadow .15s ease",
          "&:hover": { transform: "translateY(-2px)", boxShadow: 1 },
        }}
      >
        <CardActionArea
          {...actionAreaProps}
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            flexGrow: 1,
          }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={onClickCard}
        >
          {renderProductImage({
            frameSx: {
              width: { xs: 140, sm: 180 },
              minHeight: imageHeight,
              alignSelf: "stretch",
              borderRight: "1px solid",
              borderColor: "divider",
            },
            imageSx: {
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            },
          })}

          <CardContent
            sx={{
              flex: "1 1 0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0,
              }}
            >
              <Typography
                variant="subtitle1"
                title={displayName}
                sx={{
                  fontFamily: fontstyle,
                  fontWeight: 600,
                  fontSize: { xs: 15, sm: 17 },
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  pr: 1,
                }}
              >
                {displayName}
              </Typography>

              {HeartIcon}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  /* ============================ GRID VIEW ============================ */
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform .15s ease, box-shadow .15s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 1 },
      }}
    >
      <CardActionArea
        {...actionAreaProps}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={onClickCard}
      >
        {renderProductImage({
          frameSx: {
            width: "100%",
            height: imageHeight,
          },
          imageSx: {
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
          },
        })}

        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            p: compact ? 1 : 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0,
              gap: 1,
            }}
          >
            <Typography
              variant={compact ? "body2" : "subtitle2"}
              title={displayName}
              sx={{
                fontFamily: fontstyle,
                fontWeight: 400,
                display: "-webkit-box",
                WebkitLineClamp: titleLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.4,
                pr: 1,
              }}
            >
              {displayName}
            </Typography>

            {HeartIcon}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
